import api from './api';

/**
 * 变更记录服务类 - 处理网站变更相关API请求
 */
class ChangeService {
  /**
   * 获取变更记录列表
   * @param {Object} params - 查询参数
   * @param {number} params.page - 页码
   * @param {number} params.limit - 每页数量
   * @param {string} params.siteId - 站点ID (可选)
   * @param {boolean} params.isRead - 是否已读 (可选)
   * @param {string} params.sortBy - 排序字段 (可选)
   * @param {string} params.sortOrder - 排序方向 (可选)
   * @returns {Promise} 包含变更记录列表的Promise
   */
  async getChanges(params = {}) {
    try {
      const response = await api.get('/changes', { params });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * 获取单个变更记录详情
   * @param {string} changeId - 变更记录ID
   * @returns {Promise} 包含变更记录详情的Promise
   */
  async getChange(changeId) {
    try {
      const response = await api.get(`/changes/${changeId}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * 标记变更记录为已读
   * @param {string} changeId - 变更记录ID
   * @returns {Promise} 包含操作结果的Promise
   */
  async markAsRead(changeId) {
    try {
      const response = await api.patch(`/changes/${changeId}/read`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * 标记变更记录为未读
   * @param {string} changeId - 变更记录ID
   * @returns {Promise} 包含操作结果的Promise
   */
  async markAsUnread(changeId) {
    try {
      const response = await api.patch(`/changes/${changeId}/unread`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * 删除变更记录
   * @param {string} changeId - 变更记录ID
   * @returns {Promise} 包含操作结果的Promise
   */
  async deleteChange(changeId) {
    try {
      const response = await api.delete(`/changes/${changeId}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * 获取未读变更记录统计
   * @returns {Promise} 包含未读统计信息的Promise
   */
  async getUnreadStats() {
    try {
      const response = await api.get('/changes/unread-stats');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * 批量标记变更记录为已读
   * @param {Array} changeIds - 变更记录ID数组
   * @returns {Promise} 包含操作结果的Promise
   */
  async markMultipleAsRead(changeIds) {
    try {
      const response = await api.patch('/changes/batch/read', { changeIds });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * 批量删除变更记录
   * @param {Array} changeIds - 变更记录ID数组
   * @returns {Promise} 包含操作结果的Promise
   */
  async deleteMultiple(changeIds) {
    try {
      const response = await api.delete('/changes/batch', { data: { changeIds } });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * 导出变更记录
   * @param {Object} params - 导出参数
   * @param {string} params.format - 导出格式 (csv, pdf)
   * @param {string} params.siteId - 站点ID (可选)
   * @param {string} params.startDate - 开始日期 (可选)
   * @param {string} params.endDate - 结束日期 (可选)
   * @returns {Promise} 包含导出文件URL的Promise
   */
  async exportChanges(params = {}) {
    try {
      const response = await api.get('/changes/export', { 
        params,
        responseType: 'blob'
      });
      
      // 创建下载链接
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // 设置文件名
      const filename = `changes-export-${new Date().toISOString().slice(0, 10)}.${params.format || 'csv'}`;
      link.setAttribute('download', filename);
      
      // 触发下载
      document.body.appendChild(link);
      link.click();
      
      // 清理
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
      
      return { success: true, filename };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * 处理API错误
   * @param {Error} error - API错误对象
   * @returns {Error} 格式化后的错误对象
   */
  handleError(error) {
    if (error.response) {
      // 服务器响应状态码超出 2xx 范围
      const errorMsg = error.response.data.message || '操作失败，请稍后再试';
      const customError = new Error(errorMsg);
      customError.statusCode = error.response.status;
      return customError;
    } else if (error.request) {
      // 请求已发送但没有收到响应
      return new Error('无法连接到服务器，请检查您的网络连接');
    } else {
      // 请求设置时发生错误
      return new Error('发送请求时出错，请稍后再试');
    }
  }

  /**
   * 模拟获取变更记录列表（开发环境使用）
   * @returns {Array} 模拟的变更记录数据
   */
  mockGetChanges() {
    const mockData = {
      changes: [
        {
          id: '1',
          siteId: '1',
          siteName: 'example.com',
          url: 'https://example.com',
          type: 'content',
          changeDate: new Date().toISOString(),
          isRead: false,
          importance: 'high',
          summary: '首页内容发生变化，更新了产品信息'
        },
        {
          id: '2',
          siteId: '1',
          siteName: 'example.com',
          url: 'https://example.com/about',
          type: 'layout',
          changeDate: new Date(Date.now() - 86400000).toISOString(),
          isRead: true,
          importance: 'medium',
          summary: '关于页面布局调整'
        },
        {
          id: '3',
          siteId: '2',
          siteName: 'test.org',
          url: 'https://test.org',
          type: 'content',
          changeDate: new Date(Date.now() - 172800000).toISOString(),
          isRead: false,
          importance: 'low',
          summary: '细微内容更新'
        }
      ],
      pagination: {
        page: 1,
        limit: 10,
        totalItems: 3,
        totalPages: 1
      }
    };
    
    return mockData;
  }
}

const changeService = new ChangeService();
export default changeService; 